import { useContext, useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { useNavigate, useParams } from "react-router-dom";
import SimpleReactValidator from "simple-react-validator";
import { errorMessage, successMessage } from "../../../utils";
import {
  createStampCardApi,
  updateStampCardApi,
  getStampCardDetailsApi,
} from "../api";
import { AppContext } from "../../../store";

export const useAddorEditStampCard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const [forceUpdate, setForceUpdate] = useState(0);

  const [state, setState] = useImmer({
    isLoading: false,
    isSaving: false,
    data: {
      name: "",
      description: "",
      total_stamps: "",
      cycle_validity: "",
    },
  });

  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  // Fetch stamp card details if editing
  useEffect(() => {
    if (id) {
      getStampCardDetails(id);
    }
  }, [id]);

  const getStampCardDetails = async (stampCardId) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = await getStampCardDetailsApi(stampCardId);
      if (res.success) {
        setState((draft) => {
          draft.data = res.data;
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(err?.response?.data?.message || "Failed to load details");
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.data[name] = value;
    });
  };

  const handleDescriptionChange = (value) => {
    setState((draft) => {
      draft.data.description = value;
    });
  };

  const handleSubmit = async (saveAsDraft = false) => {
    if (validator.current.allValid()) {
      try {
        setState((draft) => {
          draft.isSaving = true;
        });

        const payload = {
          name: state.data.name,
          description: state.data.description,
          total_stamps: parseInt(state.data.total_stamps),
          cycle_validity: parseInt(state.data.cycle_validity),
        };

        let res;
        if (id) {
          res = await updateStampCardApi(id, payload);
        } else {
          res = await createStampCardApi(payload);
        }

        if (res.success) {
          successMessage(res.message);
          setState((draft) => {
            draft.isSaving = false;
          });
          navigate("/stamp-cards");
        }
      } catch (err) {
        errorMessage(err?.response?.data?.message || "Something went wrong");
        setState((draft) => {
          draft.isSaving = false;
        });
      }
    } else {
      validator.current.showMessages();
      setForceUpdate(forceUpdate + 1);
    }
  };

  const breadcrumbLinks = [
    { name: "Dashboard", href: "/" },
    { name: "Stamp Cards", href: "/stamp-cards" },
    { name: id ? "Edit" : "Create" },
  ];

  return {
    state,
    globalData,
    validator,
    handleInputChange,
    handleDescriptionChange,
    handleSubmit,
    breadcrumbLinks,
    isEditMode: !!id,
    forceUpdate,
  };
};
